import { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadBlob, extractErrorMessage, type Locale } from "@/shared/utils";
import { useDownloadTimesheetPreviewMutation, useDownloadTimesheetPdfMutation } from "../api";
import type { PromptState } from "./useTimesheetModals";

export interface UseTimesheetDownloadsReturn {
  downloadingTimesheetId: number | null;
  isDownloading: boolean;
  handleDownloadDocx: (id: number, year: number, month: number) => Promise<void>;
  handleDownloadPdf: (id: number, year: number, month: number) => Promise<void>;
  setPrompt: (state: PromptState | null) => void;
}

function getTimesheetFilenamePrefix(locale: Locale): string {
  switch (locale) {
    case "ru":
      return "Табель";
    case "kk":
      return "Табель";
    case "en":
      return "Timesheet";
    default:
      return "Табель";
  }
}

export function useTimesheetDownloads(setPrompt: (state: PromptState | null) => void): UseTimesheetDownloadsReturn {
  const { i18n, t } = useTranslation("TimesheetsPage");
  const locale = i18n.language as Locale;
  const [downloadTimesheetPreview] = useDownloadTimesheetPreviewMutation();
  const [downloadTimesheetPdf] = useDownloadTimesheetPdfMutation();

  const [downloadingTimesheetId, setDownloadingTimesheetId] = useState<number | null>(null);

  const isDownloading = downloadingTimesheetId !== null;

  async function handleDownloadDocx(id: number, year: number, month: number) {
    if (isDownloading) return;
    try {
      setDownloadingTimesheetId(id);
      const { blob, filename: serverFilename } = await downloadTimesheetPreview(id).unwrap();
      const prefix = getTimesheetFilenamePrefix(locale);
      const fallbackFilename = `${prefix}_${year}_${String(month).padStart(2, "0")}.docx`;
      downloadBlob(blob, serverFilename || fallbackFilename);
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.downloadErrorText", t),
        variant: "error",
      });
    } finally {
      setDownloadingTimesheetId(null);
    }
  }

  async function handleDownloadPdf(id: number, year: number, month: number) {
    if (isDownloading) return;
    try {
      setDownloadingTimesheetId(id);
      const { blob, filename: serverFilename } = await downloadTimesheetPdf(id).unwrap();
      const prefix = getTimesheetFilenamePrefix(locale);
      const fallbackFilename = `${prefix}_${year}_${String(month).padStart(2, "0")}.pdf`;
      downloadBlob(blob, serverFilename || fallbackFilename);
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.downloadErrorText", t),
        variant: "error",
      });
    } finally {
      setDownloadingTimesheetId(null);
    }
  }

  return {
    downloadingTimesheetId,
    isDownloading,
    handleDownloadDocx,
    handleDownloadPdf,
    setPrompt,
  };
}
